/**
 * Checklist storage utilities
 * Handles saving/loading progress and completion records
 */

import { devLog } from '../utils/devLog';

export const saveChecklistProgress = (checklist, completedSteps, stepTimestamps, notes, isDrillMode, startTime) => {
  if (!checklist) return;
  
  const progressKey = `emergency_progress_${checklist.id}`;
  const progress = {
    checklistId: checklist.id,
    completedSteps: Array.from(completedSteps),
    stepTimestamps: stepTimestamps,
    notes: notes,
    isDrillMode: isDrillMode,
    startTime: startTime ? (startTime instanceof Date ? startTime.toISOString() : startTime) : new Date().toISOString(),
    lastSaved: new Date().toISOString()
  };
  
  try {
    localStorage.setItem(progressKey, JSON.stringify(progress));
  } catch (error) {

  }
};

export const loadChecklistProgress = (checklistId) => {
  const savedProgressKey = `emergency_progress_${checklistId}`;
  try {
    const savedProgress = localStorage.getItem(savedProgressKey);
    if (savedProgress) {
      return JSON.parse(savedProgress);
    }
  } catch (error) {

  }
  return null;
};

export const clearChecklistProgress = (checklistId) => {
  const progressKey = `emergency_progress_${checklistId}`;
  try {
    localStorage.removeItem(progressKey);
  } catch (error) {

  }
};

export const saveChecklistCompletion = (checklist, completedSteps, stepTimestamps, notes, isDrillMode, startTime, elapsedTime, outcome = 'completed') => {
  const completionTime = new Date();
  const completionRecord = {
    checklistId: checklist.id,
    checklistTitle: checklist.title,
    checklistType: checklist.type,
    startTime: startTime instanceof Date ? startTime.toISOString() : startTime,
    completionTime: completionTime.toISOString(),
    duration: elapsedTime,
    completedSteps: Array.from(completedSteps),
    totalSteps: checklist.steps.length,
    stepTimestamps: stepTimestamps,
    notes: notes,
    isDrill: isDrillMode,
    outcome: outcome
  };

  if (isDrillMode) {
    // In drill mode, save to drill records
    const drillRecordKey = `drill_practice_${new Date().toISOString().split('T')[0]}`;
    try {
      const existingDrills = JSON.parse(localStorage.getItem(drillRecordKey) || '[]');
      existingDrills.push({
        ...completionRecord,
        type: 'emergency_drill_practice',
        drillType: checklist.type
      });
      localStorage.setItem(drillRecordKey, JSON.stringify(existingDrills));
      devLog('Drill practice recorded (not in logbook)');
    } catch (error) {

    }
  } else {
    // In real emergency mode, save to emergency records AND prepare for logbook
    const recordKey = `emergency_records_${new Date().toISOString().split('T')[0]}`;
    try {
      const existingRecords = JSON.parse(localStorage.getItem(recordKey) || '[]');
      existingRecords.push(completionRecord);
      localStorage.setItem(recordKey, JSON.stringify(existingRecords));
      
      if (outcome === 'completed') {
        // Also save to logbook queue (for when logbook is implemented)
        const logbookEntry = {
          id: `emergency_${Date.now()}`,
          timestamp: completionTime.toISOString(),
          type: 'emergency_response',
          category: 'safety',
          title: `EMERGENCY: ${checklist.title}`,
          description: `Emergency response executed: ${checklist.title}`,
          details: {
            checklistType: checklist.type,
            duration: `${Math.floor(elapsedTime / 60)}m ${elapsedTime % 60}s`,
            stepsCompleted: `${completedSteps.size}/${checklist.steps.length}`,
            startTime: startTime instanceof Date ? startTime.toISOString() : startTime,
            endTime: completionTime.toISOString(),
            notes: notes || 'No additional notes'
          },
          priority: 'critical',
          signature: 'Automated Entry',
          vesselPosition: localStorage.getItem('last_known_position') || 'Not recorded'
        };
        
        // Queue for logbook (will be written when logbook module exists)
        const logbookQueue = JSON.parse(localStorage.getItem('logbook_queue') || '[]');
        logbookQueue.push(logbookEntry);
        localStorage.setItem('logbook_queue', JSON.stringify(logbookQueue));
        
        devLog('Emergency recorded and queued for logbook entry');
      }
    } catch (error) {

    }
  }
};

export const logStepCompletion = (checklist, stepId, completed) => {
  const timestamp = new Date();
  const logKey = `emergency_log_${checklist.id}_${timestamp.toISOString().split('T')[0]}`;
  const logEntry = {
    timestamp: timestamp.toISOString(),
    action: completed ? 'completed_step' : 'uncompleted_step',
    checklistId: checklist.id,
    stepId: stepId,
    stepText: checklist.steps.find(s => (s.id || s.text) === stepId)?.text || stepId
  };
  
  try {
    const existingLog = JSON.parse(localStorage.getItem(logKey) || '[]');
    existingLog.push(logEntry);
    localStorage.setItem(logKey, JSON.stringify(existingLog));
  } catch (error) {

  }
};